// Global selector: one row above everything it scopes.
// Order: GAME (sidebar) → PLATFORM → COUNTRY → DATE RANGE → ACQUISITION SOURCE → CAMPAIGN → CREATIVE → APP VERSION → EXPERIMENT.

import type { Dimensions, DimensionValue, Environment } from '@growth/client';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { ENV_LABEL, type GlobalFilters, RANGE_PRESETS } from '../filters.ts';
import { fmtInt } from '../format.ts';
import { useApi } from '../session.tsx';

function DimSelect({ label, name, values, filters }: { label: string; name: string; values: DimensionValue[] | undefined; filters: FilterLike }) {
  const current = (filters.params as Record<string, unknown>)[name] as string | undefined;
  const known = values ?? [];
  return (
    <label className="field dim">
      <span>{label}</span>
      <select className={`input${current ? ' active' : ''}`} value={current ?? ''} onChange={(e) => filters.set({ [name]: e.target.value })}>
        <option value="">Todas</option>
        {current && !known.some((v) => v.value === current) && <option value={current}>{current}</option>}
        {known.map((v) => (
          <option key={v.value} value={v.value}>
            {v.value}
            {v.n ? ` (${fmtInt(v.n)})` : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

export function RangeSelect({ filters }: { filters: FilterLike }) {
  const p = filters.params;
  const custom = !p.last;
  return (
    <>
      <label className="field">
        <span>Rango de fechas</span>
        <select
          className="input"
          value={custom ? 'custom' : p.last}
          onChange={(e) => {
            const v = e.target.value;
            if (v === 'custom') {
              const today = new Date().toISOString().slice(0, 10);
              filters.set({ last: null, from: p.from ?? today, to: p.to ?? today });
            } else filters.set({ last: v, from: null, to: null });
          }}
        >
          {RANGE_PRESETS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
          {p.last && !RANGE_PRESETS.some((r) => r.value === p.last) && <option value={p.last}>Últimos {p.last.replace(/d$/, ' días')}</option>}
          <option value="custom">Personalizado…</option>
        </select>
      </label>
      {custom && (
        <>
          <label className="field">
            <span>Desde</span>
            <input className="input" type="date" value={p.from ?? ''} max={p.to} onChange={(e) => filters.set({ from: e.target.value })} />
          </label>
          <label className="field">
            <span>Hasta</span>
            <input className="input" type="date" value={p.to ?? ''} min={p.from} onChange={(e) => filters.set({ to: e.target.value })} />
          </label>
        </>
      )}
    </>
  );
}

export function EnvironmentSwitch({ filters }: { filters: GlobalFilters }) {
  return (
    <div className="field">
      <span>Entorno</span>
      <div className="segmented" role="group" aria-label="Entorno de datos">
        {(['production', 'staging', 'development'] as Environment[]).map((env) => (
          <button key={env} type="button" aria-pressed={filters.environment === env} onClick={() => filters.set({ environment: env === 'production' ? null : env })}>
            {ENV_LABEL[env]}
          </button>
        ))}
      </div>
    </div>
  );
}

export type FilterLike = Pick<GlobalFilters, 'params' | 'set'>;

/** Dimension selects + date range, in the selector order. Reused by the A/B compare page for segment B. */
export function DimensionFields({ filters, dims, range = true }: { filters: FilterLike; dims: Dimensions | undefined; range?: boolean }) {
  const experiments: DimensionValue[] = (dims?.experiments ?? []).flatMap((x) => [
    { value: x.experiment_key, n: x.n },
    ...x.variants.map((v) => ({ value: `${x.experiment_key}:${v}`, n: 0 })),
  ]);
  const f = filters;
  return (
    <>
      <DimSelect label="Plataforma" name="platform" values={dims?.platforms} filters={f} />
      <DimSelect label="País" name="country" values={dims?.countries} filters={f} />
      {range && <RangeSelect filters={f} />}
      <DimSelect label="Fuente" name="network" values={dims?.networks} filters={f} />
      <DimSelect label="Campaña" name="campaign" values={dims?.campaigns} filters={f} />
      <DimSelect label="Creatividad" name="creative" values={dims?.creatives} filters={f} />
      <DimSelect label="Versión" name="app_version" values={dims?.app_versions} filters={f} />
      <DimSelect label="Experimento" name="experiment" values={experiments} filters={f} />
    </>
  );
}

export const DIMENSION_KEYS = ['platform', 'country', 'network', 'campaign', 'creative', 'app_version', 'experiment'] as const;

export function useDimensions(gameId: string, environment: string, enabled = true) {
  return useApi<Dimensions>(enabled ? `dims:${gameId}:${environment}` : null, (api, signal) => api.dimensions(gameId, environment as Environment, signal));
}

export function FilterBar({ filters, dimensions = true, compare = true, environment = true }: { filters: GlobalFilters; dimensions?: boolean; compare?: boolean; environment?: boolean }) {
  const { gameId = '' } = useParams();
  const dims = useDimensions(gameId, filters.environment, dimensions);
  const active = DIMENSION_KEYS.filter((k) => (filters.params as Record<string, unknown>)[k]).length;
  const anyDim = active > 0;
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <div className={`filterbar${expanded ? ' expanded' : ''}`} role="search" aria-label="Filtros globales">
        {environment && <EnvironmentSwitch filters={filters} />}
        {dimensions ? <DimensionFields filters={filters} dims={dims.data} /> : <RangeSelect filters={filters} />}
        {dimensions && (
          <button type="button" className="btn small filters-toggle" aria-expanded={expanded} onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Menos filtros' : `Filtros${active ? ` (${active})` : ''}`}
          </button>
        )}
        {dimensions && (
          <label className="check dim">
            <input type="checkbox" checked={!!filters.params.include_internal} onChange={(e) => filters.set({ include_internal: e.target.checked ? 'true' : null })} />
            Incluir internos
          </label>
        )}
        <span className="spacer" />
        {dimensions && anyDim && (
          <button type="button" className="btn small ghost" onClick={() => filters.set({ ...Object.fromEntries(DIMENSION_KEYS.map((k) => [k, null])), include_internal: null })}>
            Limpiar filtros
          </button>
        )}
        {compare && (
          <Link className="btn small" to={{ pathname: `/g/${gameId}/compare`, search: filters.search }}>
            Comparar A/B →
          </Link>
        )}
      </div>
      {environment && filters.environment !== 'production' && (
        <div className="env-banner" role="note">
          <span className={`badge env-${filters.environment}`}>{ENV_LABEL[filters.environment]}</span>
          Estás viendo datos de {ENV_LABEL[filters.environment].toLowerCase()}. Nunca se mezclan con producción (retención, ingresos, DAU, LTV, ROAS).
        </div>
      )}
    </>
  );
}
